import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Label,
  Textarea,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  useToast,
} from '@lead-magnifier/ui'
import { Save, Plus, Trash2, Loader2, User, FileText, MessageSquare, Building2 } from 'lucide-react'
import {
  useExhibitorProfile,
  useSaveExhibitorProfile,
  type ContactPerson,
  type ExhibitorDocument,
} from '@/hooks/use-exhibitor-profile'

export const Route = createFileRoute('/_authenticated/profil/')({
  component: ProfilPage,
})

function ProfilPage() {
  const { data: profile, isLoading } = useExhibitorProfile()
  const saveProfile = useSaveExhibitorProfile()
  const { toast } = useToast()

  const [companyName, setCompanyName] = useState('')
  const [companyDescription, setCompanyDescription] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [personalMessage, setPersonalMessage] = useState('')
  const [contacts, setContacts] = useState<ContactPerson[]>([
    { name: '', role: '', email: '', phone: '' },
  ])
  const [documents, setDocuments] = useState<ExhibitorDocument[]>([])

  // Profil laden
  useEffect(() => {
    if (profile) {
      setCompanyName(profile.companyName || '')
      setCompanyDescription(profile.companyDescription || '')
      setLogoUrl(profile.logoUrl || '')
      setWebsiteUrl(profile.websiteUrl || '')
      setPersonalMessage(profile.personalMessage || '')
      setContacts(
        profile.contactPersons.length > 0
          ? profile.contactPersons
          : [{ name: '', role: '', email: '', phone: '' }]
      )
      setDocuments(profile.documents)
    }
  }, [profile])

  const handleSave = async () => {
    if (!companyName.trim()) {
      toast({ title: 'Firmenname ist erforderlich', variant: 'destructive' })
      return
    }

    try {
      await saveProfile.mutateAsync({
        companyName: companyName.trim(),
        companyDescription: companyDescription.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        websiteUrl: websiteUrl.trim() || undefined,
        personalMessage: personalMessage.trim() || undefined,
        contactPersons: contacts.filter((c) => c.name.trim()),
        documents: documents.filter((d) => d.name.trim() && d.url.trim()),
      })
      toast({ title: 'Profil gespeichert' })
    } catch (err) {
      toast({
        title: 'Fehler beim Speichern',
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
        variant: 'destructive',
      })
    }
  }

  const addContact = () => {
    setContacts([...contacts, { name: '', role: '', email: '', phone: '' }])
  }

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index))
  }

  const updateContact = (index: number, field: keyof ContactPerson, value: string) => {
    setContacts(contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c)))
  }

  const addDocument = () => {
    setDocuments([...documents, { name: '', url: '', type: 'pdf' }])
  }

  const removeDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index))
  }

  const updateDocument = (index: number, field: keyof ExhibitorDocument, value: string) => {
    setDocuments(documents.map((d, i) => (i === index ? { ...d, [field]: value } : d)))
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Mein Profil</h1>
          <p className="text-muted-foreground mt-1">
            Dein öffentliches Aussteller-Profil für Messebesucher
          </p>
        </div>
        <Button onClick={handleSave} disabled={saveProfile.isPending}>
          {saveProfile.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Speichern
        </Button>
      </div>

      {/* Firmen-Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Unternehmen
          </CardTitle>
          <CardDescription>
            Grundinformationen über dein Unternehmen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Firmenname *</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z.B. Holzbau Müller GmbH"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyDescription">Beschreibung</Label>
            <Textarea
              id="companyDescription"
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              placeholder="Was macht euer Unternehmen? Was können Besucher an eurem Stand erwarten?"
              rows={3}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="logoUrl">Logo-URL</Label>
              <Input
                id="logoUrl"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website</Label>
              <Input
                id="websiteUrl"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://www.beispiel.at"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ansprechpartner */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Ansprechpartner
          </CardTitle>
          <CardDescription>
            Kontaktpersonen, die Besucher in ihrer Messetasche sehen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {contacts.map((contact, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-2 p-4 border rounded-lg relative">
              {contacts.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={() => removeContact(i)}
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              )}
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={contact.name}
                  onChange={(e) => updateContact(i, 'name', e.target.value)}
                  placeholder="Thomas Müller"
                />
              </div>
              <div className="space-y-2">
                <Label>Funktion</Label>
                <Input
                  value={contact.role || ''}
                  onChange={(e) => updateContact(i, 'role', e.target.value)}
                  placeholder="Geschäftsführer"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={contact.email || ''}
                  onChange={(e) => updateContact(i, 'email', e.target.value)}
                  placeholder="t.mueller@firma.at"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={contact.phone || ''}
                  onChange={(e) => updateContact(i, 'phone', e.target.value)}
                  placeholder="+43 512 123456"
                />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addContact} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Ansprechpartner hinzufügen
          </Button>
        </CardContent>
      </Card>

      {/* Dokumente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumente
          </CardTitle>
          <CardDescription>
            Broschüren, Preislisten und andere Downloads für Besucher
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {documents.map((doc, i) => (
            <div key={i} className="flex gap-3 items-end">
              <div className="flex-1 space-y-2">
                <Label>Bezeichnung</Label>
                <Input
                  value={doc.name}
                  onChange={(e) => updateDocument(i, 'name', e.target.value)}
                  placeholder="Produktkatalog 2026"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Label>URL</Label>
                <Input
                  value={doc.url}
                  onChange={(e) => updateDocument(i, 'url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeDocument(i)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addDocument} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Dokument hinzufügen
          </Button>
        </CardContent>
      </Card>

      {/* Persönliche Nachricht */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Persönliche Nachricht
          </CardTitle>
          <CardDescription>
            Diese Nachricht sehen Besucher, nachdem sie deinen QR-Code gescannt haben
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={personalMessage}
            onChange={(e) => setPersonalMessage(e.target.value)}
            placeholder="z.B. Vielen Dank für Ihren Besuch an unserem Stand! Gerne senden wir Ihnen ein individuelles Angebot — antworten Sie einfach auf diese Nachricht."
            rows={4}
          />
        </CardContent>
      </Card>
    </div>
  )
}
